import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ClarityModule} from '@clr/angular';
import {
  LATEST_SCHEDULE_TIME,
  MINIMUM_LIGHT_PERIOD,
  MINUTES_IN_A_DAY,
  ThemeService
} from '@app/services/theme.service';

/** Below this width (in % of the day), a night segment is too narrow to show its moon icon */
const MINIMUM_WIDTH_FOR_ICON = 6;

/**
 * Dialog of the automatic theme switch: a bar covering the whole day with two handles. The light theme is used
 * between the handles, the dark theme outside of them. Two time fields give the same choice with the keyboard.
 * Opened from the Theme submenu of the settings dropdown (dcnetscope-main.component.html).
 */
@Component({
  selector: 'app-theme-schedule',
  standalone: true,
  imports: [CommonModule, ClarityModule],
  templateUrl: './theme-schedule.component.html',
  styleUrl: './theme-schedule.component.css'
})
export class ThemeScheduleComponent implements OnChanges {

  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  /** The schedule being edited: it is saved only with the Save button */
  lightAt = 0;
  darkAt = 0;
  /** Current time of the day, in minutes since midnight, shown as a mark on the bar */
  nowMinutes = 0;

  readonly maximumTime = MINUTES_IN_A_DAY;
  readonly hourTicks = [0, 3, 6, 9, 12, 15, 18, 21, 24];

  constructor(private themeService: ThemeService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    // Each time the dialog opens, start from the saved schedule
    if (changes['open'] && this.open) {
      const savedSchedule = this.themeService.schedule$.value;
      this.lightAt = savedSchedule.lightAt;
      this.darkAt = savedSchedule.darkAt;
      const now = new Date();
      this.nowMinutes = now.getHours() * 60 + now.getMinutes();
    }
  }

  onLightSliderInput(event: Event) {
    const slider = event.target as HTMLInputElement;
    this.setLightAt(Number(slider.value));
    // The handle may have been pushed past its limit: put it back where the value really is
    slider.value = String(this.lightAt);
  }

  onDarkSliderInput(event: Event) {
    const slider = event.target as HTMLInputElement;
    this.setDarkAt(Number(slider.value));
    slider.value = String(this.darkAt);
  }

  onLightTimeChange(event: Event) {
    const field = event.target as HTMLInputElement;
    // An empty value means the time is being typed and is not complete yet
    if (field.value !== '') {
      this.setLightAt(this.parseTime(field.value));
    }
  }

  onDarkTimeChange(event: Event) {
    const field = event.target as HTMLInputElement;
    if (field.value !== '') {
      this.setDarkAt(this.parseTime(field.value));
    }
  }

  save() {
    this.themeService.setSchedule({lightAt: this.lightAt, darkAt: this.darkAt});
    this.close();
  }

  close() {
    this.open = false;
    this.openChange.emit(false);
  }

  /** The light time stays between midnight and one hour before the dark time */
  private setLightAt(minutes: number) {
    const latestAllowed = this.darkAt - MINIMUM_LIGHT_PERIOD;
    this.lightAt = Math.max(0, Math.min(minutes, latestAllowed));
  }

  /** The dark time stays between one hour after the light time and 23:45 */
  private setDarkAt(minutes: number) {
    const earliestAllowed = this.lightAt + MINIMUM_LIGHT_PERIOD;
    this.darkAt = Math.min(LATEST_SCHEDULE_TIME, Math.max(minutes, earliestAllowed));
  }

  /** "07:30" becomes 450 */
  private parseTime(text: string): number {
    const parts = text.split(':');
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    return hours * 60 + minutes;
  }

  /** 450 becomes "07:30" */
  formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return String(hours).padStart(2, '0') + ':' + String(rest).padStart(2, '0');
  }

  /** 750 becomes "12 h 30 min" */
  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (rest === 0) {
      return `${hours} h`;
    }
    return `${hours} h ${rest} min`;
  }

  /** Position of a time on the bar, in % of the day */
  percentOfDay(minutes: number): number {
    return minutes / MINUTES_IN_A_DAY * 100;
  }

  /**
   * Position of the time label shown above a handle. The center of a slider handle does not go from 0 to 100% of
   * the slider: it stops half a handle (11px) before each end, so the label follows the same range.
   */
  handleLabelPosition(minutes: number): string {
    const fraction = minutes / MINUTES_IN_A_DAY;
    return `calc(11px + (100% - 22px) * ${fraction})`;
  }

  get lightDuration(): number {
    return this.darkAt - this.lightAt;
  }

  get darkDuration(): number {
    return MINUTES_IN_A_DAY - this.lightDuration;
  }

  get isLightNow(): boolean {
    return this.themeService.isLightTimeInSchedule(this.nowMinutes, {lightAt: this.lightAt, darkAt: this.darkAt});
  }

  get morningNightWidth(): number {
    return this.percentOfDay(this.lightAt);
  }

  get eveningNightWidth(): number {
    return 100 - this.percentOfDay(this.darkAt);
  }

  get showMorningMoon(): boolean {
    return this.morningNightWidth > MINIMUM_WIDTH_FOR_ICON;
  }

  get showEveningMoon(): boolean {
    return this.eveningNightWidth > MINIMUM_WIDTH_FOR_ICON;
  }
}
